import {inject, Service} from '@angular/core'
import {FormGroup} from '@angular/forms'
import {SummaryItem, SummarySection} from '@sparbanken-syd/sparbanken-syd-theme'

import {AccountApplication, ITaxResidencyInfo, valueTitles} from '@sparbanken-syd/spb-savings-account-backend'
import {workingAbroadOptions} from '../home/3-kyc/05-tax-residency/tax-residency.component'
import {pepStatus, pepTitles, rcaRelations} from '../home/3-kyc/06-pep/pep.component'
import {RouteService} from './route.service'
import {SavingsAccountService} from './savings-account.service'

export type WorkingAbroadValue = typeof workingAbroadOptions[number]['value']
export type PepStatusValue = typeof pepStatus[number]['value']
export type PepTitleValue = typeof pepTitles[number]['value']
export type RcaRelationsValue = typeof rcaRelations[number]['value']

@Service()
export class SummaryService {
  private readonly routeService = inject(RouteService)
  private readonly savingsAccountService = inject(SavingsAccountService)

  private readonly workingAbroadOptionsMap: Record<string, string> = Object.fromEntries(
    workingAbroadOptions.map(({value, label}) => [value, label])
  )
  private readonly pepStatusMap: Record<string, string> = Object.fromEntries(
    pepStatus.map(({value, label}) => [value, label])
  )

  private readonly pepTitleMap: Record<string, string> = Object.fromEntries(
    pepTitles.map(({value, label}) => [value, label])
  )

  private readonly rcaRelationsMap: Record<string, string> = Object.fromEntries(
    rcaRelations.map(({value, label}) => [value, label])
  )

  /**
   * Convert form data to summary sections for display
   */
  public createSummary(formData: FormGroup): SummarySection[] {
    const sections: SummarySection[] = []
    const formValue = formData.getRawValue()

    sections.push(this.createApplicantSection(formValue),
      this.createBirthSection(formValue), this.createPrimaryResidenceSection(formValue))

    if (this.hasGreenCardInfo(formValue)) {
      sections.push(this.createGreenCardSection(formValue))
    }

    if (this.hasAmericanCitizenshipInfo(formValue)) {
      sections.push(this.createAmericanCitizenshipSection(formValue))
    }

    sections.push(this.createTaxResidencySection(formValue),
      this.createPepSection(formValue), this.createBankRelationshipSection(formValue))

    // Income and Savings
    if (this.hasIncomeInfo(formValue)) {
      sections.push(this.createIncomeSection(formValue))
    }

    sections.push(this.createSavingsSection(formValue))

    return sections
  }

  private createApplicantSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const applicant = formData.applicantData

    if (applicant.personalNumber) {
      items.push({
        title: 'Personnummer',
        value: applicant.personalNumber
      })
    }

    if (applicant.name) {
      items.push({
        title: 'Namn',
        value: applicant.name
      })
    }

    if (applicant.streetAdress && applicant.streetNumber) {
      items.push({
        title: 'Adress',
        value: `${applicant.streetAdress} ${applicant.streetNumber}`
      })
    }

    if (applicant.postalCode && applicant.city) {
      items.push({
        title: 'Postnummer och ort',
        value: `${applicant.postalCode} ${applicant.city}`
      })
    }

    if (applicant.phone) {
      items.push({
        title: 'Mobilnummer',
        value: `${applicant.countryCode}${applicant.phone}`
      })
    }

    if (applicant.email) {
      items.push({
        title: 'E-post',
        value: applicant.email
      })
    }

    return {
      title: 'Kontaktuppgifter',
      items
    }
  }

  private createBirthSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const birth = formData.kycForm.birthForm

    if (birth.birthCountry) {
      items.push({
        title: 'Födelseland',
        value: birth.birthCountry
      })
    }

    if (birth.birthCity) {
      items.push({
        title: 'Födelseort',
        value: birth.birthCity
      })
    }

    // Citizenship(s)
    if (birth.citizenship && birth.citizenship.length > 0) {
      birth.citizenship.forEach((citizenship: string, index: number) => {
        if (citizenship) {
          items.push({
            title: birth.citizenship.length === 1 ? 'Medborgarskap' : `Medborgarskap ${index + 1}`,
            value: citizenship
          })
        }
      })
    }

    return {
      title: 'Medborgarskap',
      items
    }
  }

  private createPrimaryResidenceSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const residence = formData.kycForm.primaryResidenceForm

    if (residence.primaryResidence) {
      items.push({
        title: 'I vilket land har du ditt primära boende',
        value: residence.primaryResidence
      })
    }

    return {
      title: 'Primärt boende',
      items
    }
  }

  private createGreenCardSection(formData: AccountApplication): SummarySection {
    const greenCard = formData.kycForm.greenCardForm

    return {
      title: 'Green Card',
      items: [{
        title: 'Är du innehavare av amerikanskt uppehålls- och arbetstillstånd? (s.k. Green card)',
        value: greenCard.greenCard ? 'Ja' : 'Nej'
      }]
    }
  }

  private createAmericanCitizenshipSection(formData: AccountApplication): SummarySection {
    const citizenship = formData.kycForm.americanCitizenshipForm

    return {
      title: 'Amerikanskt medborgarskap',
      items: [{
        title: 'Amerikansk medborgare',
        value: citizenship.americanCitizenship ? 'Ja' : 'Nej'
      }]
    }
  }

  private createTaxResidencySection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const taxResidency = formData.kycForm.taxResidencyForm

    if (taxResidency.taxResidencySwedish !== null) {
      items.push({
        title: 'Skatterättslig hemvist i Sverige',
        value: taxResidency.taxResidencySwedish ? 'Ja' : 'Nej'
      })
    }

    if (taxResidency.taxResidencyNotSwedish !== null) {
      items.push({
        title: 'Skatterättslig hemvist i andra länder',
        value: taxResidency.taxResidencyNotSwedish ? 'Ja' : 'Nej'
      })
    }

    // Tax residency details for other countries
    if (taxResidency.taxResidencyInfo && taxResidency.taxResidencyInfo.length > 0) {
      taxResidency.taxResidencyInfo.forEach((info: ITaxResidencyInfo, index: number) => {
        if (info.taxResidencyCountry) {
          items.push({
            title: `Land skatterättslig hemvist ${taxResidency.taxResidencyInfo.length > 1 ? index + 1 : ''}`,
            value: info.taxResidencyCountry
          })
        }

        if (info.taxResidencyTin) {
          items.push({
            title: `Skatteregistreringsnummer/TIN ${taxResidency.taxResidencyInfo.length > 1 ? index + 1 : ''}`,
            value: info.taxResidencyTin
          })
        }

        if (info.taxResidencyRelation) {
          items.push({
            title: `Din relation till landet ${taxResidency.taxResidencyInfo.length > 1 ? index + 1 : ''}`,
            value: this.getWorkingAbroadText(info.taxResidencyRelation)
          })
        }

        if (info.taxResidencyOtherRelation) {
          items.push({
            title: `Din relation till landet (fritext) ${taxResidency.taxResidencyInfo.length > 1 ? index + 1 : ''}`,
            value: info.taxResidencyOtherRelation
          })
        }
      })
    }

    return {
      title: 'Skatterättslig hemvist',
      items
    }
  }

  private createPepSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const pep = formData.kycForm.pepForm

    if (pep.pepStatus) {
      items.push({
        title: 'PEP',
        value: this.getPepStatusText(pep.pepStatus)
      })
    }

    if (pep.pepStatus === 'pep' || pep.pepStatus === 'rca') {
      if (pep.pepTitle) {
        items.push({
          title: 'Arbetstitel',
          value: this.getPepTitleText(pep.pepTitle)
        })
      }

      if (pep.pepPosition) {
        items.push({
          title: 'Befattning och organisation',
          value: pep.pepPosition
        })
      }

      if (pep.pepCountry) {
        items.push({
          title: 'Land',
          value: pep.pepCountry
        })
      }

      if (pep.pepRelation && pep.pepStatus === 'rca') {
        items.push({
          title: 'Relation',
          value: this.getRcaRelationText(pep.pepRelation)
        })
      }

      if (pep.pepOtherRelation && pep.pepOtherRelationInfo) {
        items.push({
          title: 'Ytterligare personer i PEP-ställning',
          value: pep.pepOtherRelationInfo
        })
      }

      if (pep.pepOtherWork && pep.pepOtherWorkInfo) {
        items.push({
          title: 'Ytterligare arbete i PEP-ställning',
          value: pep.pepOtherWorkInfo
        })
      }
    }

    return {
      title: 'Politiskt exponerad person (PEP)',
      items
    }
  }

  private createBankRelationshipSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const bank = formData.kycForm.bankRelationshipForm

    if (bank.openSavingsAccount) {
      items.push({
        title: this.routeService.onHorseRoute() ? 'Öppna Hästsparkonto' : 'Öppna sparkonto',
        value: 'Ja'
      })
    }

    // Horse/Farm certification
    if (bank.hasHorseOrFarm) {
      items.push({
        title: 'Jag intygar att jag äger häst eller gård',
        value: 'Ja'
      })
    }

    if (bank.existingSavingsAccount) {
      items.push({
        title: 'Använd befintligt sparkonto som pengarna ska dras från',
        value: 'Ja'
      })

      if (bank.existingSavingsAccountNumber) {
        items.push({
          title: 'Kontonummer',
          value: bank.existingSavingsAccountNumber
        })
      }
    }

    if ((bank as any).openFixedRateAccount) {
      items.push({
        title: 'Öppna fasträntekonto',
        value: 'Ja'
      })

      if (bank.interestRunningTime) {
        items.push({
          title: 'Bindningstid',
          value: this.getInterestRunningTimeText(bank.interestRunningTime)
        })
      }

      if (bank.interestPlacementAmount) {
        items.push({
          title: 'Placeringsbelopp',
          value: bank.interestPlacementAmount,
          formatted: this.formatCurrency(bank.interestPlacementAmount)
        })
      }
    }

    if (bank.wantToBeContacted !== null) {
      items.push({
        title: 'Jag blir kontaktad gällande bankens övriga produkter',
        value: bank.wantToBeContacted ? 'Ja' : 'Nej'
      })
    }

    return {
      title: 'Bankrelation',
      items
    }
  }

  private createIncomeSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const income = formData.kycForm.incomeForm

    this.savingsAccountService.incomeOptions.forEach(type => {
      if (income[type.value]) {
        items.push({
          title: type.label!,
          value: income[type.value],
          formatted: this.formatCurrency(income[type.value])
        })
      }
    })

    return {
      title: 'Inkomster',
      items
    }
  }

  private createSavingsSection(formData: AccountApplication): SummarySection {
    const items: SummaryItem[] = []
    const savings = formData.kycForm.savingsForm
    if (savings.savingsTotal) {
      // Check if savingsTotal is a number (original) or a string (interval for horse route)
      const isNumber = typeof savings.savingsTotal === 'number'
      items.push({
        title: 'Totalt sparande',
        value: savings.savingsTotal,
        formatted: isNumber ? this.formatCurrency(savings.savingsTotal as number) : (savings.savingsTotal as string)
      })
    }

    if (savings.savingsSource && savings.savingsSource.length > 0) {
      const sources = savings.savingsSource.filter(Boolean)
      items.push({
        title: 'Pengarna kommer från',
        value: new Intl.ListFormat('sv', {
          style: 'long',
          type: 'conjunction'
        }).format(sources)
      })
    }

    return {
      title: 'Sparande',
      items
    }
  }

  private hasGreenCardInfo(formData: AccountApplication): boolean {
    return formData.kycForm.greenCardForm.greenCard !== null
  }

  private hasAmericanCitizenshipInfo(formData: AccountApplication): boolean {
    return formData.kycForm.americanCitizenshipForm.americanCitizenship !== null
  }

  private hasIncomeInfo(formData: AccountApplication): boolean {
    const income = formData.kycForm.incomeForm
    return Object.keys(income).some(key => income[key] !== null && income[key] !== 0)
  }

  private formatCurrency(amount: number): string {
    return `${amount.toLocaleString('sv-SE')} kr`
  }

  private getWorkingAbroadText(value: WorkingAbroadValue): string {
    return this.workingAbroadOptionsMap[value]
  }

  private getPepStatusText(value: PepStatusValue): string {
    return this.pepStatusMap[value]
  }

  private getPepTitleText(value: PepTitleValue): string {
    return this.pepTitleMap[value]
  }

  private getRcaRelationText(value: RcaRelationsValue): string {
    return this.rcaRelationsMap[value]
  }

  private getInterestRunningTimeText(months: string): string {
    return valueTitles[months]
  }
}